import {
  Activity,
  Bell,
  CalendarDays,
  GraduationCap,
  CircleDollarSign,
  ClipboardCheck,
  Contact,
  Inbox,
  Mail,
  MailQuestion,
  Settings2,
  ShieldAlert,
  Star,
  Store,
  Users,
  Hourglass,
  MapPin,
  type LucideIcon,
} from "lucide-react";
import type { Role } from "./types";

/** Who may see a route. Reviewer/Admin holds every Operator permission (tech.md section 12). */
export type Access = "O" | "A";
export type NavGroup = "queues" | "browse" | "admin";

export type AppRoute = {
  path: string;
  label: string;
  group: NavGroup;
  icon: LucideIcon;
  access: Access;
  /** The slice that builds the screen. `built` routes render a real page. */
  slice: string;
  built: boolean;
  /** One line shown on the placeholder page until the screen exists. */
  purpose: string;
  /** A real route that is not listed in the navigation or the palette. */
  hidden?: boolean;
  /** Extra words the command palette matches. */
  keywords?: string[];
};

/** One table drives the side navigation, route guards, the command palette and the placeholder
 * pages, so the whole console is navigable from the first release and a screen is added by
 * flipping `built` and supplying an element. Order is display order. */
export const APP_ROUTES: AppRoute[] = [
  {
    path: "/queues/needs-me",
    label: "Needs me",
    group: "queues",
    icon: Inbox,
    access: "O",
    slice: "U2",
    built: true,
    purpose: "Approvals, unmatched email and alerts waiting for you, most urgent first.",
    keywords: ["home", "inbox", "todo"],
  },
  {
    path: "/queues/waiting-on-host",
    label: "Waiting on host",
    group: "queues",
    icon: Hourglass,
    access: "O",
    slice: "U2",
    built: true,
    purpose: "Events where our message is the newest and the host has not replied.",
  },
  {
    path: "/queues/waiting-on-vendors",
    label: "Waiting on vendors",
    group: "queues",
    icon: Store,
    access: "O",
    slice: "U2",
    built: true,
    purpose: "Open RFQs, expired quotes and vendors not yet confirmed.",
  },
  {
    path: "/queues/money",
    label: "Money",
    group: "queues",
    icon: CircleDollarSign,
    access: "O",
    slice: "U6",
    built: true,
    purpose: "Payouts to approve or held, refunds requested and open disputes.",
    keywords: ["payouts", "refunds", "disputes"],
  },
  {
    path: "/queues/problems",
    label: "Problems",
    group: "queues",
    icon: ShieldAlert,
    access: "O",
    slice: "U3",
    built: true,
    purpose: "Failed sends, dead jobs, blocked attachments and bounces.",
    keywords: ["failed", "errors"],
  },

  {
    path: "/events",
    label: "All events",
    group: "browse",
    icon: CalendarDays,
    access: "O",
    slice: "U2",
    built: true,
    purpose: "Every event, with filters, search and saved views.",
    keywords: ["search"],
  },
  {
    path: "/approvals",
    label: "Approvals",
    group: "browse",
    icon: ClipboardCheck,
    access: "O",
    slice: "U2",
    built: true,
    purpose: "Proposals waiting for a decision, with the live gate check beside each.",
    keywords: ["proposals"],
  },
  {
    path: "/intake",
    label: "Intake review",
    group: "browse",
    icon: MailQuestion,
    access: "O",
    slice: "U3",
    built: true,
    purpose: "Email we could not place: match it to an event, accept a new host, or discard it.",
    keywords: ["unmatched", "spam"],
  },
  {
    path: "/inbox",
    label: "Inbox",
    group: "browse",
    icon: Mail,
    access: "O",
    slice: "post-M9",
    built: true,
    purpose: "Email anyone from your own mailbox; replies come back to your queue.",
    keywords: ["email", "compose", "mailbox"],
  },
  {
    path: "/hosts",
    label: "Hosts",
    group: "browse",
    icon: Contact,
    access: "O",
    slice: "U2",
    built: true,
    purpose: "Hosts, their events and their persona.",
  },
  {
    path: "/vendors",
    label: "Vendors",
    group: "browse",
    icon: Store,
    access: "O",
    slice: "U4",
    built: true,
    purpose: "The vendor directory, profiles and payout readiness.",
  },
  {
    path: "/ratings",
    label: "Ratings",
    group: "browse",
    icon: Star,
    access: "O",
    slice: "U7",
    built: true,
    purpose: "Host feedback waiting to be anonymized and approved.",
  },
  {
    path: "/interest",
    label: "Interest",
    group: "browse",
    icon: MapPin,
    access: "O",
    slice: "interest-signups",
    built: true,
    purpose: "People who asked to hear when we launch near them, by city, with an export.",
    keywords: ["notify", "waitlist", "signups", "leads", "analytics"],
  },
  {
    path: "/operations",
    label: "Operations",
    group: "browse",
    icon: Activity,
    access: "O",
    slice: "U7",
    built: true,
    purpose: "Email health, queue depths, escalations and response rates.",
    keywords: ["dashboard", "metrics"],
  },

  {
    path: "/notifications",
    label: "Notifications",
    group: "browse",
    icon: Bell,
    access: "O",
    slice: "U3",
    built: true,
    hidden: true,
    purpose: "Alerts for you, grouped by kind, each linking to the screen that deals with it.",
  },
  {
    path: "/payouts",
    label: "Payouts",
    group: "browse",
    icon: CircleDollarSign,
    access: "O",
    slice: "U6",
    built: true,
    hidden: true,
    purpose: "Payouts by state, with the blockers and the money invariant beside each.",
    keywords: ["money", "transfer"],
  },
  {
    path: "/refunds",
    label: "Refunds",
    group: "browse",
    icon: CircleDollarSign,
    access: "O",
    slice: "U6",
    built: true,
    hidden: true,
    purpose: "Refunds requested and decided, with what each takes back from the providers.",
    keywords: ["money"],
  },
  {
    path: "/disputes",
    label: "Problems",
    group: "browse",
    icon: ShieldAlert,
    access: "O",
    slice: "U6",
    built: true,
    hidden: true,
    purpose: "Problems a host reported and chargebacks the card network opened.",
    keywords: ["money", "dispute", "chargeback"],
  },
  {
    path: "/operations/failures",
    label: "Failed jobs and sends",
    group: "browse",
    icon: ShieldAlert,
    access: "O",
    slice: "U3",
    built: true,
    hidden: true,
    purpose: "Dead jobs and sends, with retry and cancel.",
  },

  {
    path: "/training",
    label: "Training",
    group: "browse",
    icon: GraduationCap,
    access: "O",
    slice: "U8b",
    built: true,
    hidden: true,
    purpose: "A guided, hands-on tour of the console on practice data.",
    keywords: ["tour", "tutorial", "walkthrough", "onboarding", "learn"],
  },

  {
    path: "/config",
    label: "Configuration",
    group: "admin",
    icon: Settings2,
    access: "O",
    slice: "U2",
    built: true,
    purpose: "Templates, policies, pricing and escalation settings, with version history.",
    keywords: ["settings", "templates"],
  },
  {
    path: "/admin/users",
    label: "Users",
    group: "admin",
    icon: Users,
    access: "A",
    slice: "U1",
    built: true,
    purpose: "People who can sign in, their roles and authenticators.",
    keywords: ["roles", "people", "team"],
  },
];

export const NAV_GROUPS: { id: NavGroup; label: string }[] = [
  { id: "queues", label: "Work queues" },
  { id: "browse", label: "Browse" },
  { id: "admin", label: "Administration" },
];

export const HOME = "/queues/needs-me";

export function canAccess(access: Access, roles: readonly string[]): boolean {
  if (access === "O") return roles.includes("operator") || roles.includes("reviewer_admin");
  return roles.includes("reviewer_admin");
}

export const visibleRoutes = (roles: readonly string[]) =>
  APP_ROUTES.filter((r) => !r.hidden && canAccess(r.access, roles));

export function roleLabel(roles: readonly Role[] | readonly string[]): string {
  return roles.includes("reviewer_admin") ? "Reviewer/Admin" : "Operator";
}
